import { inject, Pipe, type PipeTransform } from '@angular/core';
import { LocalizationService } from '@services/logic/localization.service';

/**
 * `Intl.NumberFormat.formatRange` exists in every supported runtime but is
 * missing from the ES2022 lib typings this project compiles against.
 */
interface NumberFormatWithRange extends Intl.NumberFormat {
  formatRange?(start: number, end: number): string;
}

/**
 * Formats a major-unit amount range with the correct currency symbol and
 * locale.
 *
 * Usage: {{ 150 | formatCurrencyRange: 750 }}
 * Example output: "150-750 kr." (handyhand.dk)
 *
 * The currency defaults to the host country's (`LocalizationService.
 * currencyCode`); pass one explicitly only to override, e.g.
 * {{ 150 | formatCurrencyRange: 750 : 'eur' }}. Input amounts are in MAJOR
 * units, matching FormatCurrencyPipe — minor-unit API amounts must be divided
 * at the call site (`value / 100`), the pipe never divides.
 *
 * `pure: false` — re-evaluates after the async localization-config API fetch
 * settles, same pattern as FormatCurrencyPipe.
 */
@Pipe({
  name: 'formatCurrencyRange',
  pure: false,
})
export class FormatCurrencyRangePipe implements PipeTransform {
  private readonly localizationService = inject(LocalizationService);

  transform(
    from: number | null | undefined,
    to: number | null | undefined,
    currency?: string | null,
  ): string {
    if (
      from === null ||
      from === undefined ||
      to === null ||
      to === undefined ||
      !Number.isFinite(from) ||
      !Number.isFinite(to)
    ) {
      return '';
    }

    const resolvedCurrency =
      currency ?? this.localizationService.currencyCode();

    if (!resolvedCurrency) {
      return `${from}-${to}`;
    }

    const bcp47 = this.localizationService.bcp47();

    try {
      const formatter: NumberFormatWithRange = new Intl.NumberFormat(bcp47, {
        style: 'currency',
        currency: resolvedCurrency.toUpperCase(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
      if (formatter.formatRange) {
        return formatter.formatRange(from, to);
      }
      return `${formatter.format(from)}-${formatter.format(to)}`;
    } catch {
      return `${from}-${to} ${resolvedCurrency}`;
    }
  }
}
